import { useEffect, useRef, useState } from 'react'
import { View } from 'react-native'
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated'

import { animationConfig, SegmentedProps } from './types'
import SegmentedItem from './SegmentedItem'

const height = 32
const padding = 8
const gap = 8 // matches the container's `gap-2`
const hideConfig = { duration: 220 }

// Cumulative left of each item from measured *widths* (position-independent),
// so the indicator stays correct immediately after a reorder/resize.
function computeLefts(
  options: { id: string }[],
  elements: Record<string, { width: number }>
) {
  let cursor = padding
  return options.map((o) => {
    const left = cursor
    cursor += (elements[o.id]?.width ?? 0) + gap
    return left
  })
}

export default function Segmented<T extends string>({
  options = [],
  value,
  onChange,
  disabled,
  classNameOption,
}: SegmentedProps<T>) {
  const isInitialized = useRef(false)
  // Id animated optimistically on press; the effect skips its redundant spring.
  const optimisticId = useRef<string | null>(null)
  const prevOptionsKey = useRef('')
  // Last shown selection, so a dropped selection animates out from where it
  // actually sat rather than from the contracted layout.
  const lastIndex = useRef(0)
  const lastCount = useRef(0)
  const lastWidth = useRef(0)
  const lastLeft = useRef(0)
  const isVisible = useRef(false)

  const animation = useSharedValue(0)
  // Selection presence (0..1): indicator opacity + gates the active labels.
  const selection = useSharedValue(value ? 1 : 0)
  // Indicator x, owned by the effect (not derived) so it survives option changes.
  const indicatorLeft = useSharedValue(0)

  const [elements, setElements] = useState<Record<string, { width: number }>>(
    {}
  )

  const animated_width = useSharedValue(
    (value?.id && elements[value?.id]?.width) ?? 0
  )

  const styleIndicator = useAnimatedStyle(() => ({
    width: animated_width.value,
    left: indicatorLeft.value,
    opacity: selection.value,
  }))

  // Optimistic: animate on press so the tap feels instant, before `value`
  // round-trips back through `onChange`. The effect skips the redundant spring.
  const handleSelect = (option: (typeof options)[number]) => {
    const index = options.findIndex((o) => o.id === option.id)
    const el = elements[option.id]

    optimisticId.current = option.id
    selection.set(1)
    animation.set(withSpring(index, animationConfig))
    indicatorLeft.set(
      withSpring(
        computeLefts(options, elements)[index] ?? padding,
        animationConfig
      )
    )
    if (el) animated_width.set(withSpring(el.width, animationConfig))

    onChange?.(option)
  }

  useEffect(() => {
    const index = value ? options.findIndex((o) => o.id === value.id) : -1
    const hasSelection = index >= 0

    const optionsKey = options.map((o) => o.id).join('|')
    const didOptionsChange = optionsKey !== prevOptionsKey.current
    prevOptionsKey.current = optionsKey

    // No valid selection: slide the indicator out over the nearest edge and fade
    // (container is `overflow-hidden`). Slides from its last real position, so a
    // selection dropped off the end just fades where it already was.
    if (!hasSelection) {
      if (!isVisible.current) return
      isVisible.current = false

      // Exit toward the side it sat on (index-based, not pixels).
      const exitsLeft = lastIndex.current < lastCount.current / 2
      const target = exitsLeft
        ? -(lastWidth.current + padding)
        : lastLeft.current + lastWidth.current + padding

      indicatorLeft.set(withTiming(target, hideConfig))
      selection.set(withTiming(0, hideConfig))
      return
    }

    // Snap `animation` (label fade) and `indicatorLeft` into place on an option-
    // set change; spring only when picking a different segment within a stable set.
    const firstInit = !isInitialized.current
    const wasHidden = !isVisible.current
    const shouldSnap = firstInit || didOptionsChange
    // Already animated on press (handleSelect); skip the redundant spring when
    // `value` catches up — but not across an option-set change, which must snap.
    const isOptimistic = optimisticId.current === value?.id && !shouldSnap
    optimisticId.current = null

    isInitialized.current = true
    isVisible.current = true
    lastIndex.current = index
    lastCount.current = options.length

    const targetLeft = computeLefts(options, elements)[index] ?? padding
    lastLeft.current = targetLeft

    const el = value ? elements[value.id] : undefined
    if (el) lastWidth.current = el.width

    if (isOptimistic) return

    if (el) {
      const snapWidth = shouldSnap || animated_width.value === 0
      animated_width.set(
        snapWidth ? el.width : withSpring(el.width, animationConfig)
      )
    }

    animation.set(shouldSnap ? index : withSpring(index, animationConfig))
    indicatorLeft.set(
      shouldSnap ? targetLeft : withSpring(targetLeft, animationConfig)
    )

    // Reveal: fade in when returning from hidden; snap on first mount.
    selection.set(wasHidden && !firstInit ? withTiming(1, hideConfig) : 1)
  }, [value, options, elements, animation, animated_width, selection, indicatorLeft])

  if (!(options?.length > 0)) return null

  return (
    <View
      className="flex w-max max-w-full flex-row items-center gap-2 self-start overflow-hidden rounded-full border border-black/10 bg-neutral-50 p-2"
      style={{ padding }}
    >
      {options.length > 0 && (
        <Animated.View
          className="absolute rounded-full bg-text"
          style={[
            {
              top: padding,
              height,
            },
            styleIndicator,
          ]}
        />
      )}
      {options.map((option, index) => {
        const isSelected = value?.id === option.id
        const isDisabled = !!(disabled || option.disabled)

        return (
          <SegmentedItem
            key={option.id}
            option={option}
            isSelected={isSelected}
            isDisabled={isDisabled}
            onChange={handleSelect}
            index={index}
            animation={animation}
            selection={selection}
            classNameOption={classNameOption}
            onLayout={(event) => {
              const { width } = event.nativeEvent.layout
              setElements((prev) => ({
                ...prev,
                [option.id]: { width },
              }))
            }}
          />
        )
      })}
    </View>
  )
}
